import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface Props {
  url: string;
}

const PdfPreview: React.FC<Props> = ({ url }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);
  const [pdf, setPdf] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!url) return;
      setLoading(true);
      try {
        const doc = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        setPdf(doc);
        setTotalPages(doc.numPages);
        setPage(1);
      } catch (e) {
        console.error('PDF load error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [url]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;

    const render = async () => {
      setLoading(true);
      try {
        const p = await pdf.getPage(page);
        if (cancelled) return;
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext('2d')!;
        const viewport = p.getViewport({ scale: 1.5 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }
        renderTaskRef.current = p.render({ canvasContext: ctx, viewport });
        await renderTaskRef.current.promise;
        renderTaskRef.current = null;
      } catch (e: any) {
        if (e?.name !== 'RenderingCancelledException') console.error('PDF render error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    render();
    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdf, page]);

  return (
    <div className="w-full h-full flex flex-col items-center overflow-auto bg-slate-100 dark:bg-slate-800 p-4 gap-3">
      {totalPages > 1 && (
        <div className="flex items-center gap-3 shrink-0">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 rounded-lg bg-white dark:bg-slate-700 shadow text-sm disabled:opacity-40"
          >
            &lsaquo;
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Seite {page} / {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 rounded-lg bg-white dark:bg-slate-700 shadow text-sm disabled:opacity-40"
          >
            &rsaquo;
          </button>
        </div>
      )}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 rounded-xl">
            <svg className="w-8 h-8 animate-spin text-[#216dab]" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
        )}
        <canvas ref={canvasRef} className="max-w-full rounded-xl shadow-lg" />
      </div>
    </div>
  );
};

export default PdfPreview;
